import { useCallback, useMemo, useRef } from 'react';

import { useTheme } from '@mui/material';

import useBaseChartOptions from '@/components/Chart/hooks/options/useBaseChartOptions';
import getFullscreenTooltip from '@/components/Chart/hooks/tooltip/getFullscreenTooltip';
import useSpotTooltipFormatter from '@/components/Chart/hooks/tooltip/useSpotTooltipFormatter';
import type {
  ExtendedOptions,
  ExtendedSeriesOptionsType
} from '@/components/Chart/types/highcharts';
import { applySeriesVisibility } from '@/components/Chart/utils/seriesVisibility';
import {
  DEFAULT_CHART_HEIGHT,
  fullScreenChartPalette,
  regularChartPalette
} from '@/constants/constants';
import { Permission } from '@/entities/permissions';
import type { SymbolKey } from '@/entities/symbol';
import useSelectionFunctions from '@/features/SymbolSelector/hooks/useSelectionFunctions';
import usePermission from '@/shared/hooks/usePermission';

import useChartFunctions, {
  getStandardWatermarkAttributes,
  getWatermarkConfig
} from './useChartFunctions';

import type { SeriesClickEventObject } from 'highcharts';
import type Highcharts from 'highcharts/highstock';

const STOCK_TOOLS_BUTTONS = [
  'indicators',
  'simpleShapes',
  'lines',
  'crookedLines',
  'measure',
  'advanced',
  'toggleAnnotations',
  'verticalLabels',
  'flags',
  'zoomChange',
  'currentPriceIndicator'
] as const;

const useSeriesClick = () => {
  const { addSymbolToSelection, isSymbolSelected, removeSymbolFromSelection } =
    useSelectionFunctions();

  const onSeriesClick = (event: SeriesClickEventObject) => {
    // @ts-expect-error: highcharts rules.
    const metadata: SymbolKey = event.point.series.userOptions.custom;

    const symbol: SymbolKey = {
      Code: metadata.Code,
      Period: metadata.Period,
      PeriodType: metadata.PeriodType,
      TimeRef: metadata.TimeRef
    };
    if (isSymbolSelected(symbol)) {
      removeSymbolFromSelection(symbol);
    } else {
      addSymbolToSelection(symbol);
    }
  };
  return { onSeriesClick };
};

const assignFullScreenSeriesColor = (
  series: ExtendedSeriesOptionsType,
  isLightMode: boolean
): void => {
  const { combinedIndex } = series.custom;
  const palette = isLightMode ? fullScreenChartPalette : regularChartPalette;

  series.color ??=
    combinedIndex > 5
      ? '#F8F8F8'
      : (series.custom.color ?? palette[combinedIndex % palette.length]);
};

const useFullScreenOptions = ({
  height,
  isDataHub,
  isLightMode
}: {
  height?: number;
  isDataHub?: boolean;
  isLightMode: boolean;
}) => {
  const { hasPermission } = usePermission();
  const useSmallWatermark = hasPermission(Permission.UseSmallChartWatermark);
  const theme = useTheme();
  const createTooltipFormatter = useSpotTooltipFormatter(isDataHub ? 'Full Title' : undefined);

  const tooltip = useMemo(
    () => getFullscreenTooltip(createTooltipFormatter, theme).tooltip,
    [createTooltipFormatter, theme]
  );

  const { createPlotOptions, createEventOptions } = useChartFunctions();
  const { createBaseChartOptions } = useBaseChartOptions(height ?? DEFAULT_CHART_HEIGHT);

  const { onSeriesClick } = useSeriesClick();

  const plotOptions = createPlotOptions();

  if (!isDataHub && plotOptions.series) {
    plotOptions.series.events = {
      click: onSeriesClick
    };
  }

  const watermarkImage = useRef<Highcharts.SVGElement | null>(null);
  const getWatermarkImageRef = useCallback(() => watermarkImage, []);
  const textColor = isLightMode ? theme.palette.background.paper : theme.palette.text.primary;
  const backgroundColor = isLightMode ? theme.palette.text.primary : theme.palette.background.paper;
  const watermarkImageName = isLightMode ? '/watermark-color.svg' : '/watermark-black.svg';
  const { aspectRatio } = getWatermarkConfig(watermarkImageName);

  const getWatermarkAttributes = (chart: Highcharts.Chart) => {
    return getStandardWatermarkAttributes({
      chart,
      aspectRatio,
      useSmallWatermark
    });
  };

  const { events, exportEvents } = createEventOptions(
    getWatermarkImageRef,
    getWatermarkAttributes,
    watermarkImageName
  );

  const createOptions = useCallback(
    (chartSeries: ExtendedSeriesOptionsType[], hiddenSeriesIds: string[]): ExtendedOptions => {
      chartSeries.forEach(series => {
        assignFullScreenSeriesColor(series, isLightMode);
      });

      const baseOptions = createBaseChartOptions(
        applySeriesVisibility(chartSeries, hiddenSeriesIds) ?? chartSeries,
        textColor,
        backgroundColor
      );

      return {
        ...baseOptions,
        chart: { ...baseOptions.chart, events },
        plotOptions,
        exporting: {
          ...baseOptions.exporting,
          chartOptions: {
            ...baseOptions.exporting?.chartOptions,
            chart: { events: exportEvents, backgroundColor }
          },
          sourceWidth: 1000,
          sourceHeight: 420,
          buttons: undefined
        },
        stockTools: { gui: { enabled: true, buttons: [...STOCK_TOOLS_BUTTONS] } },
        tooltip
      };
    },
    [
      createBaseChartOptions,
      textColor,
      backgroundColor,
      events,
      exportEvents,
      plotOptions,
      tooltip,
      isLightMode
    ]
  );

  return { createOptions };
};

export default useFullScreenOptions;
